import { useMemo } from "react";
import useActiveAnalysesStream from "../../../../../../../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../../../../../../../api/useProjects";
import { useTranslations } from "../../../../../../../../../../api/useTranslations";
import t from "../../../../../../../../../../i18n";
import { computeMeanOverPeriod } from "diagnostics-domain/aggregationPeriod";
import {
  AGGREGATE_NO_SYSTEM,
  AGGREGATE_UNEXPLAINED_LOSSES,
  useAvailableResources,
} from "../../../../../../../hook/useAvailableResources";
import type { FaultDataset, QuantityDataset } from "../../../../../../../hook/useDataset";
import type { BlockDiagramModelT } from "../../../../../../../schema/block/diagram";
import { getAvailableResourceKind } from "../../../../../../../utils/getAvailableResourceKind";
import { useAnnotationAnalysis } from "../../AnalysisSelector";
import { groupBy } from "diagnostics-lib/iterable";
import { uniq, uniqBy } from "diagnostics-lib/array";
import {
  groupSystemEventRowsByEventId,
  sumSystemEventImpactsByDate,
  sumSystemEventsImpactsByDate,
} from "./systemEventImpacts";
export type AnnotationValue = {
  column: string;
  quantity: string | null;
  value: string | number | boolean | null;
  thresholdValue: number | null;
  evolution: 1 | -1 | 0 | null;
  unit: string;
  name?: string;
  priority?: string;
  event?: { id: string; type: string; faultName: string; startsAt: string; hasMultiFault: boolean };
};

export type AnnotationAggregatedValue = {
  min: number | null;
  max: number | null;
  hasInvalidities: boolean;
  hasMultipleFaultStatuses: boolean;
  hasMultiplePIs?: boolean;
};

export type Annotation = BlockDiagramModelT["config"]["layers"][number] & {
  id: string;
  name: string;
  type: string | null;
  description: string | null;
  values: (AnnotationValue & AnnotationAggregatedValue)[];
};

type AnnotationDataset<T extends QuantityDataset | FaultDataset> = {
  current: T;
  previous: T | null;
};

const COLUMN_ORDER = [
  "validity",
  "faultStatus",
  "measured",
  "std",
  "expected",
  "gap",
  "real",
  "quantityImpacts",
  "externalImpacts",
  "normalizedGap",
];
const EVOLUTION_THRESHOLD = 20;

export const useAnnotations = (
  project: Project,
  block: BlockDiagramModelT,
  quantities: QuantityDataset,
  faults: FaultDataset,
) => {
  const availableResources = useAvailableResources({ projectId: project.id });
  const translations = useTranslations();
  const { activeAnalyses } = useActiveAnalysesStream(project.id);
  const [annotationAnalysis] = useAnnotationAnalysis(block, project.id);
  const analyses = useMemo(
    () =>
      Array.from(
        activeAnalyses.filter(analysis => analysis.date <= (annotationAnalysis?.date ?? Infinity)),
      ).sort((a, b) => a.date - b.date),
    [activeAnalyses, annotationAnalysis?.date],
  );
  const currentAnalysis = analyses[analyses.length - 1];
  const previousAnalysis = analyses[analyses.length - 2];
  const currentDate = currentAnalysis?.date;
  const previousDate = previousAnalysis?.date;

  const aggregate = block.config.aggregate;

  const annotationQuantityDataset: { current: QuantityDataset; previous: QuantityDataset | null } =
    useMemo(
      () => ({
        current: aggregate ? quantities : quantities.filter(row => row.date === currentDate),
        previous: aggregate ? null : quantities.filter(row => row.date === previousDate),
      }),
      [aggregate, currentDate, previousDate, quantities],
    );

  const annotationFaultDataset: { current: FaultDataset; previous: FaultDataset | null } = useMemo(
    () => ({
      current: aggregate ? faults : faults.filter(row => row.date === currentDate),
      previous: aggregate ? null : faults.filter(row => row.date === previousDate),
    }),
    [aggregate, currentDate, faults, previousDate],
  );
  const annotationNameTranslations = useMemo(
    () => ({
      AGGREGATE_UNEXPLAINED_LOSSES: t("charts.legend.unexplained_losses", translations),
      AGGREGATE_NO_SYSTEM: t("project.dashboard.editor.gui.no_system"),
    }),
    [translations],
  );

  const annotations = useMemo(
    () =>
      block.config.layers.map((layer, i) => {
        const identifier = layer.encodings.map(encoding => encoding.identifier)[0];

        const resource = availableResources.fault.identifiers.find(x => x.name === identifier);
        const identifierDisplayName = resource?.displayName;

        const annotationType = identifier
          ? getAvailableResourceKind(availableResources, identifier)
          : null;

        let annotationName = "";
        if (typeof layer.name === "string" && layer.name.length > 0) {
          annotationName = layer.name;
        } else if (identifier === AGGREGATE_UNEXPLAINED_LOSSES) {
          annotationName = annotationNameTranslations.AGGREGATE_UNEXPLAINED_LOSSES;
        } else if (identifier === AGGREGATE_NO_SYSTEM) {
          annotationName = annotationNameTranslations.AGGREGATE_NO_SYSTEM;
        } else {
          annotationName = identifierDisplayName ?? identifier ?? `Annotation ${i + 1}`;
        }

        const annotationDescription =
          annotationType === "QUANTITY"
            ? annotationQuantityDataset.current
            : annotationType === "FAULT"
              ? annotationFaultDataset.current
              : [];
        const annotationMatchDescription = annotationDescription.find(
          row => row.identifier === identifier,
        );
        const description =
          typeof annotationMatchDescription?.description === "string"
            ? annotationMatchDescription.description
            : "";

        /** @note can't compute evolution over period, only for specific analysis */
        const annotationEvolutions = block.data.period ? [] : layer.evolutions;

        const annotation: Annotation = {
          ...layer,
          id: `annotation-${i}`,
          type: annotationType,
          name: annotationName,
          description,
          values: computeAnnotationValues(
            Boolean(block.config.aggregate),
            block.data.transforms,
            annotationType,
            identifier,
            layer.encodings,
            annotationEvolutions,
            annotationQuantityDataset,
            annotationFaultDataset,
            availableResources,
          ),
        };

        return annotation;
      }),
    [
      annotationFaultDataset,
      annotationNameTranslations.AGGREGATE_NO_SYSTEM,
      annotationNameTranslations.AGGREGATE_UNEXPLAINED_LOSSES,
      annotationQuantityDataset,
      availableResources,
      block.config.layers,
      block.data.period,
      block.data.transforms,
      block.config.aggregate,
    ],
  );

  return annotations;
};

function computeAnnotationValues(
  temporalAggregation: boolean,
  dataTransforms: BlockDiagramModelT["data"]["transforms"],
  type: ReturnType<typeof getAvailableResourceKind>,
  identifier: string | undefined,
  encodings: BlockDiagramModelT["config"]["layers"][number]["encodings"],
  evolutions: BlockDiagramModelT["config"]["layers"][number]["evolutions"],
  quantities: AnnotationDataset<QuantityDataset>,
  faults: AnnotationDataset<FaultDataset>,
  availableResources: ReturnType<typeof useAvailableResources>,
) {
  let values: Annotation["values"] = [];
  const uniqQuantityEncoding = uniqBy(encodings, "quantity");
  const hasMultiplePIs =
    uniqQuantityEncoding.filter(f =>
      availableResources.performanceIndicators.some(pi => pi.name === f.quantity),
    ).length > 1;

  if (type === "QUANTITY") {
    values = computeQuantityAnnotationValues(temporalAggregation, encodings, evolutions, {
      current: quantities.current.filter(row => row.identifier === identifier),
      previous: quantities.previous,
    });
  }

  if (type === "FAULT") {
    values = computeFaultAnnotationValues(
      temporalAggregation,
      encodings,
      evolutions,
      {
        current: faults.current.filter(row => row.identifier === identifier),
        previous: faults.previous,
      },
      hasMultiplePIs,
    );
  }

  if (type === "SYSTEM") {
    const distributedByEvent = isDistributedByEvent(identifier ?? "", dataTransforms);
    const filteredCurrentFaults = faults.current.filter(row => row.systemId === identifier);
    const faultData = {
      current: filteredCurrentFaults,
      previous: faults.previous,
    };
    const performanceIndicators = availableResources.performanceIndicators
      .filter((pi): pi is typeof pi & { unit: string } => typeof pi.unit === "string")
      .map(pi => ({ name: pi.name, unit: pi.unit }));
    values = computeSystemAnnotationValues(
      temporalAggregation,
      encodings,
      evolutions,
      faultData,
      hasMultiplePIs,
      performanceIndicators,
    ).sort((a, b) => (a.quantity ?? "").localeCompare(b.quantity ?? ""));

    if (distributedByEvent) {
      const activePowerQuantity = availableResources.performanceIndicators.find(
        q => q.isActivePower,
      );

      const eventValues = computeSystemEventAnnotationValues(
        temporalAggregation,
        encodings,
        faultData,
        availableResources.quantity.identifiers,
        performanceIndicators,
      );
      const activePowerValues = values.filter(v => v.quantity === activePowerQuantity?.name);
      const otherValues = values.filter(v => v.quantity !== activePowerQuantity?.name);
      values = [...activePowerValues, ...eventValues, ...otherValues];
    }
    return values;
  }

  if (type === "AGGREGATE" && identifier === AGGREGATE_UNEXPLAINED_LOSSES) {
    values = computeUnexplainedLossesAnnotationValues(
      temporalAggregation,
      encodings,
      evolutions,
      faults,
      quantities,
      hasMultiplePIs,
    );
  }

  return values.sort((a: Annotation["values"][number], b: Annotation["values"][number]) => {
    if (a?.column !== b?.column) {
      return COLUMN_ORDER.indexOf(a.column) - COLUMN_ORDER.indexOf(b.column);
    }
    if (a?.quantity && b?.quantity) {
      return (
        availableResources.performanceIndicators.findIndex(p => p?.name === a.quantity) -
        availableResources.performanceIndicators.findIndex(p => p?.name === b.quantity)
      );
    }
    return 0;
  });
}

function computeQuantityAnnotationValues(
  temporalAggregation: boolean,
  encodings: BlockDiagramModelT["config"]["layers"][number]["encodings"],
  evolutions: BlockDiagramModelT["config"]["layers"][number]["evolutions"],
  annotationQuantityDataset: AnnotationDataset<QuantityDataset>,
) {
  return encodings.map(({ identifier, column, quantity }) => {
    const quantityColumn = column as keyof QuantityDataset[number];
    const annotationValue: Annotation["values"][number] = {
      column: column ?? "",
      quantity: null,
      value: null,
      min: null,
      max: null,
      unit:
        column === "normalizedGap" ? "σ" : `${annotationQuantityDataset.current[0]?.unit ?? ""}`,
      evolution: null,
      thresholdValue: null,
      hasInvalidities: annotationQuantityDataset.current.some(row =>
        row["validity"]?.toString().includes("INVALID"),
      ),
      hasMultipleFaultStatuses: false,
    };

    switch (column) {
      case "validity": {
        annotationValue.value = formatValue(
          annotationQuantityDataset.current?.[0]?.[quantityColumn],
        );
        break;
      }
      case "measured":
      case "std":
      case "expected":
      case "gap":
      case "normalizedGap": {
        if (temporalAggregation) {
          const { value, min, max } = computeAnnotationValueMedian(
            annotationQuantityDataset.current.filter(
              row =>
                row["validity"]?.toString() === "VALID_MANUAL" ||
                row["validity"]?.toString() === "VALID_AUTO",
            ),
            column,
          );
          annotationValue.value = formatValue(value);
          annotationValue.min = min;
          annotationValue.max = max;
        } else {
          annotationValue.value = formatValue(
            annotationQuantityDataset.current?.[0]?.[quantityColumn],
          );
          if (shouldComputeEvolutions(identifier, column, evolutions)) {
            const previousValue = annotationQuantityDataset.previous?.[0]?.[quantityColumn];
            if (typeof annotationValue.value === "number" && typeof previousValue === "number") {
              annotationValue.evolution = evolutionValue(
                previousValue,
                annotationValue.value,
                needAbsoluteValueEvolution(column),
              );
            }
          }
          if (column === "gap") {
            const thresholdValue = annotationQuantityDataset.current?.[0]?.["normalizedGap"];
            annotationValue.thresholdValue =
              typeof thresholdValue === "number" ? thresholdValue : null;
          }
        }
        break;
      }
      /** @note External Gap only work for 1 analysis */
      case "externalImpacts": {
        const key = `externalImpacts/${quantity}`;

        const unit = findUnitFromDataset(
          annotationQuantityDataset.current,
          `externalImpactsUnit/${quantity}`,
        );
        if (temporalAggregation) {
          annotationValue.unit = unit;
          annotationValue.quantity = quantity ?? "";
          const { value, min, max } = computeAnnotationValueAggregateFromUnit(
            key,
            annotationValue.unit,
            annotationQuantityDataset.current,
          );
          annotationValue.value = value;
          annotationValue.min = min;
          annotationValue.max = max;
        } else {
          annotationValue.value =
            annotationQuantityDataset.current?.[0]?.[
              key as keyof (typeof annotationQuantityDataset.current)[0]
            ] ?? null;
          annotationValue.unit = unit;
          annotationValue.quantity = quantity ?? "";

          if (shouldComputeEvolutions(identifier, column, evolutions)) {
            const previousData = annotationQuantityDataset.previous?.[0];
            const previousValue = previousData?.[key as keyof typeof previousData];
            if (typeof previousValue === "number" && typeof annotationValue.value === "number") {
              annotationValue.evolution = evolutionValue(
                previousValue,
                annotationValue.value,
                false,
              );
            }
          }
        }
        break;
      }
      default:
        break;
    }
    return annotationValue;
  });
}

function computeFaultAnnotationValues(
  temporalAggregation: boolean,
  encodings: BlockDiagramModelT["config"]["layers"][number]["encodings"],
  evolutions: BlockDiagramModelT["config"]["layers"][number]["evolutions"],
  annotationFaultDataset: AnnotationDataset<FaultDataset>,
  hasMultiplePIs: boolean,
) {
  return encodings.map(({ identifier, column, quantity }) => {
    const faultColumn = column as keyof FaultDataset[number];
    const statuses = new Set(
      annotationFaultDataset.current
        .filter(row => row.identifier === identifier)
        .map(row => row["faultStatus"])
        .filter(status => typeof status === "string"),
    );
    const annotationValue: Annotation["values"][number] = {
      column: column ?? "",
      quantity: null,
      value: null,
      min: null,
      max: null,
      unit: "",
      evolution: null,
      thresholdValue: null,
      hasInvalidities: false,
      hasMultipleFaultStatuses: statuses.size > 1,
      hasMultiplePIs,
    };
    switch (column) {
      case "faultStatus": {
        annotationValue.value = formatValue(Array.from(statuses)[0]);
        break;
      }
      case "real":
        annotationValue.unit = annotationFaultDataset.current[0]?.unit?.toString() ?? "";
        if (temporalAggregation) {
          const { value, min, max } = computeAnnotationValueMedian(
            annotationFaultDataset.current,
            column,
          );
          annotationValue.value = formatValue(value);
          annotationValue.min = min;
          annotationValue.max = max;
        } else {
          annotationValue.value = formatValue(annotationFaultDataset.current?.[0]?.[faultColumn]);
          if (shouldComputeEvolutions(identifier, column, evolutions)) {
            const previousValue = annotationFaultDataset.previous?.[0]?.[faultColumn];
            if (typeof annotationValue.value === "number" && typeof previousValue === "number") {
              annotationValue.evolution = evolutionValue(
                previousValue,
                annotationValue.value,
                needAbsoluteValueEvolution(column),
              );
            }
          }
        }
        break;
      case "quantityImpacts": {
        const key = `${column}/${quantity}` as keyof FaultDataset[number];
        annotationValue.quantity = quantity ?? "";
        annotationValue.unit = findUnitFromDataset(
          annotationFaultDataset.current,
          `impactUnit/${quantity}`,
        );

        if (temporalAggregation) {
          if (annotationValue.unit === "MW" || annotationValue.unit === "MWe") {
            annotationValue.value = formatValue(
              computeAnnotationValueMeanOverPeriod(annotationFaultDataset.current, key),
            );
          } else {
            const { value, min, max } = computeAnnotationValueMedian(
              annotationFaultDataset.current,
              key,
            );
            annotationValue.value = formatValue(value);
            annotationValue.min = min;
            annotationValue.max = max;
          }
        } else {
          annotationValue.value = formatValue(annotationFaultDataset.current?.[0]?.[key]);
          if (shouldComputeEvolutions(identifier, column, evolutions)) {
            const previousValue = annotationFaultDataset.previous?.[0]?.[key];
            if (typeof annotationValue.value === "number" && typeof previousValue === "number") {
              annotationValue.evolution = evolutionValue(
                previousValue,
                annotationValue.value,
                needAbsoluteValueEvolution(column),
              );
            }
          }
        }

        break;
      }
      default:
        break;
    }
    return annotationValue;
  });
}

const findUnitFromDataset = (
  dataset: { [key: string]: string | number | boolean | undefined | null }[],
  impactKey: string,
) => {
  const results = dataset.find(row => typeof row[impactKey] === "string");

  return results?.[impactKey]?.toString() ?? "";
};

function computeSystemAnnotationValues(
  temporalAggregation: boolean,
  encodings: BlockDiagramModelT["config"]["layers"][number]["encodings"],
  evolutions: BlockDiagramModelT["config"]["layers"][number]["evolutions"],
  annotationFaultDataset: AnnotationDataset<FaultDataset>,
  hasMultiplePIs: boolean,
  performanceIndicators: { name: string; unit: string }[],
) {
  return encodings.map(({ identifier, column, quantity }) => {
    const annotationValue: Annotation["values"][number] = {
      column: column ?? "",
      quantity: quantity ?? "",
      value: null,
      min: null,
      max: null,
      unit: "",
      evolution: null,
      thresholdValue: null,
      hasInvalidities: false,
      hasMultipleFaultStatuses: false,
      hasMultiplePIs,
    };
    switch (column) {
      case "systemImpact": {
        const unitKey = `impactUnit/${quantity}`;
        annotationValue.unit =
          performanceIndicators.find(i => i.name === quantity)?.unit ??
          findUnitFromDataset(annotationFaultDataset.current, unitKey);

        const key = `${column}/${quantity}` as keyof FaultDataset[number];
        if (temporalAggregation) {
          if (annotationValue.unit === "MW" || annotationValue.unit === "MWe") {
            const v = computeAnnotationValueMeanOverPeriod(
              uniqBy(annotationFaultDataset.current, "date"),
              key,
            );
            annotationValue.value = formatValue(v);
          } else {
            const { value, min, max } = computeAnnotationValueMedian(
              annotationFaultDataset.current,
              key,
            );
            annotationValue.value = formatValue(value);
            annotationValue.min = min;
            annotationValue.max = max;
          }
        } else {
          annotationValue.value = formatValue(annotationFaultDataset.current?.[0]?.[key]);
          if (shouldComputeEvolutions(identifier, column, evolutions)) {
            const previousValue = annotationFaultDataset.previous?.[0]?.[key];
            if (typeof annotationValue.value === "number" && typeof previousValue === "number") {
              annotationValue.evolution = evolutionValue(
                previousValue,
                annotationValue.value,
                needAbsoluteValueEvolution(column),
              );
            }
          }
        }
        break;
      }
      default:
        break;
    }
    return annotationValue;
  });
}

function computePaddingSystemEvent(
  datesDiagram: number[],
  faults: FaultDataset[number][],
  startAtEvent: number,
  endAtEvent: number,
  key: string,
) {
  const paddedRows: FaultDataset[number][] = [];
  const template = faults[0];
  if (!template) return paddedRows;

  const summedFaultsByDate = sumSystemEventImpactsByDate(faults, key as keyof FaultDataset[number]);

  for (const date of datesDiagram.filter(d => d < startAtEvent)) {
    paddedRows.push({ ...template, date, [key]: 0 });
  }

  paddedRows.push(...summedFaultsByDate);

  for (const date of datesDiagram.filter(d => d > endAtEvent)) {
    paddedRows.push({ ...template, date, [key]: 0 });
  }

  return paddedRows;
}

function computeSystemEventAnnotationValues(
  temporalAggregation: boolean,
  encodings: BlockDiagramModelT["config"]["layers"][number]["encodings"],
  annotationFaultDataset: AnnotationDataset<FaultDataset>,
  availableResources: ReturnType<typeof useAvailableResources>["quantity"]["identifiers"],
  performanceIndicators: { name: string; unit: string }[],
) {
  const activePower = availableResources.find(q => q.isActivePower);
  const activePowerEncoding = encodings.find(encoding => encoding.quantity === activePower?.name);

  if (!activePower || !activePowerEncoding) return [];
  const activePowerQuantity = activePowerEncoding?.quantity ?? "";

  const highOrMediumPriorityEventFaults = annotationFaultDataset.current.filter(
    row =>
      (row.eventPriority === "HIGH" || row.eventPriority === "MEDIUM") &&
      typeof row.eventId === "string",
  );
  const key = `quantityImpacts/${activePowerQuantity}` as keyof FaultDataset[number];
  const highOrMediumPriorityEvents = groupSystemEventRowsByEventId(
    highOrMediumPriorityEventFaults,
  ).sort((aRows, bRows) => {
    const aPriority = getMaxPriority(aRows);
    const bPriority = getMaxPriority(bRows);
    const aName = aRows[0]?.eventName?.toString() ?? "";
    const bName = bRows[0]?.eventName?.toString() ?? "";
    return bPriority === aPriority ? aName.localeCompare(bName) : bPriority - aPriority;
  });

  const dates = uniq(annotationFaultDataset.current.map(row => Number(row.date)));

  const annotations: Annotation["values"] = [];

  const impactUnit =
    performanceIndicators.find(i => i.name === activePowerQuantity)?.unit ??
    findUnitFromDataset(annotationFaultDataset.current, `impactUnit/${activePowerQuantity}`);

  for (const rows of highOrMediumPriorityEvents) {
    const summedRowsByDate = sumSystemEventImpactsByDate(rows, key);
    if (
      !summedRowsByDate[0] ||
      typeof summedRowsByDate[0]?.eventType !== "string" ||
      typeof summedRowsByDate[0]?.eventId !== "string" ||
      typeof summedRowsByDate[0].eventStartsAt !== "number" ||
      typeof summedRowsByDate[0].eventEndsAt !== "number"
    ) {
      continue;
    }

    const {
      eventId,
      eventType,
      eventName = "",
      eventHasMultiFault,
      eventStartsAt,
      eventFaultName,
      eventEndsAt,
    } = summedRowsByDate[0];

    const annotationValue: Annotation["values"][number] = {
      name: eventName?.toString(),
      priority:
        typeof summedRowsByDate[0]?.eventPriority === "string"
          ? String(summedRowsByDate[0]?.eventPriority)
          : "",
      column: "eventImpact",
      quantity: activePowerQuantity ?? "",
      value: null,
      min: null,
      max: null,
      unit: "",
      evolution: null,
      thresholdValue: null,
      hasInvalidities: false,
      hasMultipleFaultStatuses: false,
      event: {
        id: eventId,
        type: eventType,
        faultName: (eventFaultName ?? "").toString(),
        startsAt: new Date(eventStartsAt).toISOString(),
        hasMultiFault: eventHasMultiFault === true,
      },
    };
    annotationValue.unit = impactUnit;
    if (temporalAggregation) {
      if (annotationValue.unit === "MW" || annotationValue.unit === "MWe") {
        const paddedEventValues = uniqBy(
          computePaddingSystemEvent(dates, rows, eventStartsAt, eventEndsAt, key),
          "date",
        );
        const v = computeAnnotationValueMeanOverPeriod(paddedEventValues, key);

        annotationValue.value = formatValue(v);
      }
    } else {
      annotationValue.value = formatValue(summedRowsByDate[0]?.[key]);
    }

    annotations.push(annotationValue);
  }

  const lowPriorityEventFaults = annotationFaultDataset.current.filter(
    row => row.eventPriority === "LOW" && typeof row.eventId === "string",
  );

  if (lowPriorityEventFaults.length > 0) {
    const lowPriorityEvents = groupSystemEventRowsByEventId(lowPriorityEventFaults);
    const lowPriorityEventCount = lowPriorityEvents.length;

    const annotationValue: Annotation["values"][number] = {
      name: t("blocks.diagram.annotations.value.system_by_event.other", lowPriorityEventCount),
      priority: "LOW",
      column: "eventImpact",
      quantity: activePowerQuantity ?? "",
      value: null,
      min: null,
      max: null,
      unit: "",
      evolution: null,
      thresholdValue: null,
      hasInvalidities: false,
      hasMultipleFaultStatuses: false,
    };

    annotationValue.unit = impactUnit;
    const summedLowPriorityEventImpacts: { date: number; value: number }[] = [];
    for (const row of lowPriorityEventFaults) {
      const date = Number(row.date);
      summedLowPriorityEventImpacts.push({
        date,
        value: (row[key] as number) ?? 0,
      });
    }

    if (temporalAggregation) {
      const paddedLowPriorityEvents = lowPriorityEvents.flatMap(rows => {
        const event = rows[0];
        if (typeof event?.eventStartsAt !== "number" || typeof event.eventEndsAt !== "number") {
          return [];
        }

        return [
          computePaddingSystemEvent(dates, rows, event.eventStartsAt, event.eventEndsAt, key),
        ];
      });
      const totalSummedLowPriorityEventImpacts = sumSystemEventsImpactsByDate(
        paddedLowPriorityEvents,
        dates,
        key,
      );

      const v = computeMeanOverPeriod({
        analysisDates: totalSummedLowPriorityEventImpacts.map(item => item.date),
        values: totalSummedLowPriorityEventImpacts.map(item => item.value),
      });
      annotationValue.value = formatValue(v);
    } else {
      const sum = summedLowPriorityEventImpacts.reduce((acc, curr) => acc + curr.value, 0);
      annotationValue.value = formatValue(sum);
    }

    annotations.push(annotationValue);
  }

  return annotations;
}

function computeUnexplainedLossesAnnotationValues(
  temporalAggregation: boolean,
  encodings: BlockDiagramModelT["config"]["layers"][number]["encodings"],
  evolutions: BlockDiagramModelT["config"]["layers"][number]["evolutions"],
  annotationFaultDataset: AnnotationDataset<FaultDataset>,
  annotationQuantityDataset: AnnotationDataset<QuantityDataset>,
  hasMultiplePIs: boolean,
) {
  return encodings.map(({ identifier, column, quantity }) => {
    const annotationValue: Annotation["values"][number] = {
      column: column ?? "",
      quantity: quantity ?? "",
      value: null,
      min: null,
      max: null,
      unit: "",
      evolution: null,
      thresholdValue: null,
      hasInvalidities: false,
      hasMultipleFaultStatuses:
        new Set(annotationFaultDataset.current.map(row => row["faultStatus"])).size > 1,
      hasMultiplePIs,
    };
    const explainedLossesKey = `explainedLosses/${quantity}` as keyof FaultDataset[number];

    annotationValue.unit =
      annotationQuantityDataset.current
        .find(row => row.identifier === quantity)
        ?.unit?.toString() ?? "";
    switch (column) {
      case "unexplainedLosses":
        if (temporalAggregation) {
          const faultDatasetByDate = groupBy(annotationFaultDataset.current, "date");
          const quantityRows = annotationQuantityDataset.current.filter(
            row =>
              row.identifier === quantity &&
              (row.validity === "VALID_AUTO" || row.validity === "VALID_MANUAL"),
          );
          const unit = quantityRows[0]?.unit ?? "";
          const [analysisDates, values] = quantityRows.reduce(
            (acc, row) => {
              acc[0].push(Number(row.date));
              const explainedLosses =
                faultDatasetByDate.get(row.date)?.[0]?.[explainedLossesKey] ?? 0;
              if (typeof row.gap === "number" && typeof explainedLosses === "number") {
                acc[1].push(row.gap - explainedLosses);
              } else {
                acc[1].push(0);
              }
              return acc;
            },
            [[], []] as [number[], number[]],
          );
          if (unit === "MW" || unit === "MWe") {
            annotationValue.value = formatValue(computeMeanOverPeriod({ analysisDates, values }));
          } else {
            const results = values
              .filter(n => {
                if (n === null || n === undefined || typeof n !== "number") return false;
                return !Number.isNaN(n);
              })
              .sort((a, b) => a - b);
            const half = Math.floor(values.length / 2);
            annotationValue.value = formatValue(results[half]);
            annotationValue.min = Math.min(...results);
            annotationValue.max = Math.max(...results);
          }
        } else {
          const currentQuantityGap = annotationQuantityDataset.current.find(
            row => row.identifier === quantity,
          )?.["gap"];
          const currentExplainedLosses =
            annotationFaultDataset.current[0]?.[explainedLossesKey] ?? 0;
          annotationValue.value =
            typeof currentQuantityGap === "number" && typeof currentExplainedLosses === "number"
              ? formatValue(currentQuantityGap - currentExplainedLosses)
              : null;

          if (shouldComputeEvolutions(identifier, column, evolutions)) {
            const previousQuantityGap = annotationQuantityDataset.previous?.[0]?.["gap"];
            const previousExplainedLosses =
              annotationFaultDataset.previous?.[0]?.[explainedLossesKey] ?? 0;
            const previousValue =
              typeof previousQuantityGap === "number" && typeof previousExplainedLosses === "number"
                ? previousQuantityGap - previousExplainedLosses
                : null;
            if (typeof annotationValue.value === "number" && typeof previousValue === "number") {
              annotationValue.value = evolutionValue(
                previousValue,
                annotationValue.value,
                needAbsoluteValueEvolution(explainedLossesKey),
              );
            }
          }
        }
        break;
      default:
        break;
    }
    return annotationValue;
  });
}

function shouldComputeEvolutions(
  identifier: string,
  column: string | null,
  evolutions: BlockDiagramModelT["config"]["layers"][number]["evolutions"],
) {
  return (
    evolutions.length > 0 &&
    evolutions.some(ev => ev.identifier === identifier && ev.column === column)
  );
}

function evolutionValue(a: number, b: number, isAbsolute = false) {
  if (a === 0) return 0;
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  const e = isAbsolute
    ? ((Math.abs(b) - Math.abs(a)) / Math.abs(a)) * 100
    : ((b - a) / Math.abs(a)) * 100;
  return e < -EVOLUTION_THRESHOLD ? -1 : e > EVOLUTION_THRESHOLD ? 1 : 0;
}

function formatValue(value: string | number | boolean | null | undefined) {
  return typeof value === "number" ? Number(Number(value)) : (value ?? null);
}

function needAbsoluteValueEvolution(column: string): boolean {
  if (column === "measured" || column === "expected" || column === "real") {
    return false;
  }
  return true;
}

function computeAnnotationValueAggregateFromUnit(
  key: string,
  unit: string,
  dataset: QuantityDataset | FaultDataset,
) {
  const out: {
    value: string | number | boolean | null;
    min: number | null;
    max: number | null;
  } = { value: null, min: null, max: null };
  if (unit === "MW" || unit === "MWe") {
    out.value = formatValue(computeAnnotationValueMeanOverPeriod(dataset, key));
  } else {
    const { value, min, max } = computeAnnotationValueMedian(dataset, key);
    out.value = formatValue(value);
    out.min = min;
    out.max = max;
  }
  return out;
}

function computeAnnotationValueMeanOverPeriod(
  dataset: QuantityDataset | FaultDataset,
  key: string,
) {
  type Key = keyof (typeof dataset)[number];
  if (dataset.every(row => row[key as Key] === null)) return null;

  const [analysisDates, values] = dataset.reduce(
    (acc, row) => {
      acc[0].push(Number(row["date"]));
      acc[1].push(Number(row[key as Key] ?? 0));
      return acc;
    },
    [[], []] as [number[], number[]],
  );

  return computeMeanOverPeriod({ analysisDates, values });
}

function computeAnnotationValueMedian(dataset: QuantityDataset | FaultDataset, key: string) {
  const values = dataset
    .map(row => row[key as keyof (typeof dataset)[number]])
    .filter((n): n is number => {
      if (n === null || n === undefined || typeof n !== "number") return false;
      return !Number.isNaN(n);
    })
    .sort((a, b) => a - b);
  const half = Math.floor(values.length / 2);
  return { value: values[half], min: Math.min(...values), max: Math.max(...values) };
}

const PRIORITY_ORDER: Record<string, number> = { HIGH: 3, MEDIUM: 2, LOW: 1 };
const getMaxPriority = (rows: FaultDataset[number][]) =>
  Math.max(
    ...rows.map(row =>
      typeof row.eventPriority === "string" ? (PRIORITY_ORDER[row.eventPriority] ?? 0) : 0,
    ),
  );

function isDistributedByEvent(
  identifier: string,
  transforms: BlockDiagramModelT["data"]["transforms"],
) {
  const systemTransform = transforms?.find(t =>
    t.filter?.every(
      filter =>
        filter.column === "faultStatus" ||
        (filter.column === "systemId" && filter.value.includes(identifier)),
    ),
  );
  if (!systemTransform) return false;

  return Boolean(systemTransform.groupBy?.includes("eventName"));
}
